import api from './apiClient';
import { calculateFileUploadTimeout } from '../utils/apiTimeouts';
import { FileAttachment } from '../types';

export const fileService = {
  uploadFile: async (
    file: File,
    fileType: string,
    petId?: number,
    vaccinationId?: number,
    medicalRecordId?: number,
    medicalControlId?: number,
    groomControlId?: number
  ): Promise<FileAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    if (petId) {
      formData.append('pet_id', petId.toString());
    }
    if (vaccinationId) {
      formData.append('vaccination_id', vaccinationId.toString());
    }
    if (medicalRecordId) {
      formData.append('medical_record_id', medicalRecordId.toString());
    }
    if (medicalControlId) {
      formData.append('medical_control_id', medicalControlId.toString());
    }
    if (groomControlId) {
      formData.append('groom_control_id', groomControlId.toString());
    }

    const timeout = await calculateFileUploadTimeout(file.size);

    try {
      const response = await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: timeout,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return response.data;
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('La subida del archivo tardó demasiado. Por favor, verifica tu conexión a internet e intenta nuevamente con una imagen más pequeña.');
      }
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        throw new Error('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }
      throw error;
    }
  },

  uploadFilePublic: async (
    file: File,
    fileType: string,
    petId: number,
    token: string,
    vaccinationId?: number,
    medicalRecordId?: number,
    medicalControlId?: number
  ): Promise<FileAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    formData.append('pet_id', petId.toString());
    if (vaccinationId) {
      formData.append('vaccination_id', vaccinationId.toString());
    }
    if (medicalRecordId) {
      formData.append('medical_record_id', medicalRecordId.toString());
    }
    if (medicalControlId) {
      formData.append('medical_control_id', medicalControlId.toString());
    }

    const timeout = await calculateFileUploadTimeout(file.size);

    try {
      const response = await api.post('/files/public/upload', formData, {
        params: { token },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: timeout,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return response.data;
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('La subida del archivo tardó demasiado. Por favor, verifica tu conexión a internet e intenta nuevamente con una imagen más pequeña.');
      }
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        throw new Error('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }
      throw error;
    }
  },

  getPetFiles: async (petId: number): Promise<FileAttachment[]> => {
    const response = await api.get(`/files/pet/${petId}`);
    return response.data;
  },

  deleteFile: async (fileId: number): Promise<void> => {
    await api.delete(`/files/${fileId}`);
  },

  getPresignedUploadUrl: async (
    fileType: string,
    petId: number,
    filename: string,
    contentType: string,
    fileSize: number,
    medicalRecordId?: number,
    medicalControlId?: number,
    vaccinationId?: number
  ): Promise<{ upload_url: string; key: string; filename: string }> => {
    const response = await api.post('/files/presigned-upload-url', {
      file_type: fileType,
      pet_id: petId,
      filename: filename,
      content_type: contentType,
      file_size: fileSize,
      medical_record_id: medicalRecordId,
      medical_control_id: medicalControlId,
      vaccination_id: vaccinationId,
    });
    return response.data;
  },

  getPublicPresignedUploadUrl: async (
    fileType: string,
    petId: number,
    filename: string,
    contentType: string,
    fileSize: number,
    token: string,
    medicalRecordId?: number,
    medicalControlId?: number,
    vaccinationId?: number
  ): Promise<{ upload_url: string; key: string; filename: string }> => {
    const response = await api.post(
      '/files/public/presigned-upload-url',
      {
        file_type: fileType,
        pet_id: petId,
        filename: filename,
        content_type: contentType,
        file_size: fileSize,
        medical_record_id: medicalRecordId,
        medical_control_id: medicalControlId,
        vaccination_id: vaccinationId,
      },
      { params: { token } }
    );
    return response.data;
  },

  confirmFileUpload: async (
    filename: string,
    fileType: string,
    petId: number,
    originalFilename: string,
    fileSize: number,
    contentType: string,
    medicalRecordId?: number,
    medicalControlId?: number,
    vaccinationId?: number
  ): Promise<FileAttachment> => {
    const response = await api.post('/files/confirm-upload', {
      filename: filename,
      file_type: fileType,
      pet_id: petId,
      original_filename: originalFilename,
      file_size: fileSize,
      content_type: contentType,
      medical_record_id: medicalRecordId,
      medical_control_id: medicalControlId,
      vaccination_id: vaccinationId,
    });
    return response.data;
  },

  confirmPublicFileUpload: async (
    filename: string,
    fileType: string,
    petId: number,
    originalFilename: string,
    fileSize: number,
    contentType: string,
    token: string,
    medicalRecordId?: number,
    medicalControlId?: number,
    vaccinationId?: number
  ): Promise<FileAttachment> => {
    const response = await api.post(
      '/files/public/confirm-upload',
      {
        filename: filename,
        file_type: fileType,
        pet_id: petId,
        original_filename: originalFilename,
        file_size: fileSize,
        content_type: contentType,
        medical_record_id: medicalRecordId,
        medical_control_id: medicalControlId,
        vaccination_id: vaccinationId,
      },
      { params: { token } }
    );
    return response.data;
  },
};








